import { cn } from '@/lib/utils';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import type { IStep } from '@chainlit/react-client';

import { Loader } from '@/components/Loader';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { Translator } from 'components/i18n';

import ThinkingStream, { THINKING_STEP_NAME } from './ThinkingStream';

interface Props {
  step: IStep;
  isRunning?: boolean;
}

export default function Step({
  step,
  children,
  isRunning
}: PropsWithChildren<Props>) {
  const using = useMemo(() => {
    return isRunning && step.start && !step.end && !step.isError;
  }, [step, isRunning]);

  const hasContent = step.input || step.output || step.steps?.length;
  const isError = step.isError;
  const stepName = step.name;

  const [openValue, setOpenValue] = useState<string>(
    step.defaultOpen ? step.id : ''
  );

  // Auto-collapse when step finishes if autoCollapse is set
  useEffect(() => {
    if (!using && step.autoCollapse) {
      setOpenValue('');
    }
  }, [using, step.autoCollapse]);

  if (step.name === THINKING_STEP_NAME) {
    return <ThinkingStream step={step} isRunning={isRunning} />;
  }

  // If there's no content, just render the status without accordion
  if (!hasContent) {
    return (
      <div className="flex flex-col flex-grow w-0">
        <p
          className={cn(
            'flex items-center gap-1 text-sm font-medium tracking-tight',
            isError && 'text-destructive',
            !using && 'text-muted-foreground',
            using && 'loading-shimmer text-foreground'
          )}
          id={`step-${stepName}`}
        >
          {using ? (
            <>
              <Translator path="chat.messages.status.using" /> {stepName}
            </>
          ) : (
            <>
              <Translator path="chat.messages.status.used" /> {stepName}
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow w-0">
      <Accordion
        type="single"
        collapsible
        value={openValue}
        onValueChange={(val) => setOpenValue(val)}
        className="w-full"
      >
        <AccordionItem value={step.id} className="border-none">
          <AccordionTrigger
            className={cn(
              'group flex items-center gap-1.5 justify-start p-0 text-sm font-medium tracking-tight',
              'transition-colors hover:no-underline',
              isError && 'text-destructive',
              !using && 'text-muted-foreground hover:text-foreground',
              using && 'loading-shimmer text-foreground'
            )}
            id={`step-${stepName}`}
          >
            {using ? (
              <>
                <Translator path="chat.messages.status.using" /> {stepName}
              </>
            ) : (
              <>
                <Translator path="chat.messages.status.used" /> {stepName}
              </>
            )}
          </AccordionTrigger>
          <AccordionContent>
            <div
              className={cn(
                'flex-grow mt-3 ml-1 pl-4 border-l-2 transition-colors',
                using ? 'border-primary' : 'border-border'
              )}
            >
              {children}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {using ? (
        <>
          <Loader />
        </>
      ) : null}
    </div>
  );
}
